// Helpers compartilhados entre Pages/SantoLog/Maps/index.js (pedidos)
// e Pages/SantoLog/Maps/Areas/index.js (cadastro de áreas).

// Tolerância para considerar dois pontos como o mesmo (≈ 1mm na superfície terrestre)
export const COORD_EPS = 1e-9

// Remove pontos consecutivos iguais e o ponto de fechamento (se já estiver lá)
export function dedupeAndOpen(coords) {
  if (!coords?.length) return []
  const out = []
  for (const p of coords) {
    const last = out[out.length - 1]
    if (!last || Math.abs(last.lat - p.lat) > COORD_EPS || Math.abs(last.lng - p.lng) > COORD_EPS) {
      out.push({ lat: Number(p.lat), lng: Number(p.lng) })
    }
  }
  if (out.length > 1) {
    const f = out[0], l = out[out.length - 1]
    if (Math.abs(f.lat - l.lat) < COORD_EPS && Math.abs(f.lng - l.lng) < COORD_EPS) out.pop()
  }
  return out
}

// Local [{lat,lng}] → GeoJSON Polygon (lng-lat, anel fechado, idempotente)
// Garante explicitamente que ring[0] e ring[last] tenham valores numéricos idênticos
// (mesmo `Number()` aplicado em uma única fonte) — assim o backend não rejeita por
// "anel não fechado" devido a diferença de precisão.
export function coordsToGeom(coords) {
  const cleaned = dedupeAndOpen(coords)
  if (cleaned.length < 3) return null
  const firstLng = Number(cleaned[0].lng)
  const firstLat = Number(cleaned[0].lat)
  const ring = cleaned.map(c => [Number(c.lng), Number(c.lat)])
  ring[0] = [firstLng, firstLat]            // canonicaliza o ponto inicial
  ring.push([firstLng, firstLat])           // ponto final = ponto inicial (mesmos valores)
  return { type: 'Polygon', coordinates: [ring] }
}

// Detecta a ordem dos pares (lat,lng) no anel a partir da faixa de latitude (|lat| ≤ 90)
export function detectAxisOrder(ring) {
  if (!ring?.length) return 'lng-lat'
  let p0Over90 = 0, p1Over90 = 0
  for (const p of ring) {
    if (Math.abs(Number(p[0])) > 90) p0Over90++
    if (Math.abs(Number(p[1])) > 90) p1Over90++
  }
  if (p0Over90 && !p1Over90) return 'lng-lat'
  if (p1Over90 && !p0Over90) return 'lat-lng'
  return 'lat-lng' // backend SantoLog devolve [lat, lng]
}

// GeoJSON polygon (qualquer ordem) → [{lat,lng}], aberto e sem duplicatas
export function geomToCoords(geom) {
  if (!geom) return []
  let parsed = geom
  if (typeof geom === 'string') { try { parsed = JSON.parse(geom) } catch { return [] } }
  const ring = parsed?.coordinates?.[0] || []
  const order = detectAxisOrder(ring)
  const coords = order === 'lng-lat'
    ? ring.map(p => ({ lat: Number(p[1]), lng: Number(p[0]) }))
    : ring.map(p => ({ lat: Number(p[0]), lng: Number(p[1]) }))
  return dedupeAndOpen(coords)
}

// Mapeia uma área vinda da API para o formato local consumido pelos dois mapas
export function apiToLocal(a, fallbackColor = '#00e5a0') {
  return {
    id: a.id,
    name: a.name,
    color: a.color || fallbackColor,
    coordinates: geomToCoords(a.geom),
    is_active: a.is_active !== false,
    carrier_id: a.carrier_id ?? a.carrierId ?? a.carrier?.id ?? null,
    carrier_name: a.carrier?.name ?? a.carrier_name ?? null,
    driver_id: a.driver_id ?? a.driverId ?? a.driver?.id ?? null,
    driver_name: a.driver?.name ?? a.driver_name ?? null,
  }
}

// Área em m² (esférica) — fórmula da projeção das coordenadas no esferóide
export function polygonAreaM2(coords) {
  if (!coords || coords.length < 3) return 0
  const R = 6378137
  const toRad = d => d * Math.PI / 180
  let total = 0
  for (let i = 0, n = coords.length; i < n; i++) {
    const a = coords[i], b = coords[(i + 1) % n]
    total += toRad(b.lng - a.lng) * (2 + Math.sin(toRad(a.lat)) + Math.sin(toRad(b.lat)))
  }
  return Math.abs(total * R * R / 2)
}

export function fmtArea(m2) {
  if (m2 < 1_000_000) return `${(m2 / 1000).toFixed(1).replace('.', ',')} mil m²`
  return `${(m2 / 1_000_000).toFixed(2).replace('.', ',')} km²`
}

// Ray-casting point-in-polygon (verts no formato [{lat,lng}])
export function pointInPoly(lat, lng, verts) {
  if (!verts?.length) return false
  let inside = false
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const xi = verts[i].lng, yi = verts[i].lat
    const xj = verts[j].lng, yj = verts[j].lat
    if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) inside = !inside
  }
  return inside
}

// ─── Operações booleanas de polígonos via polygon-clipping ──────────────────
// O lib trabalha com Multipolygon: [ [ [ [x,y], ... ] ] ]  ou Polygon: [ [ [x,y], ... ] ]
// Convertemos nosso formato local [{lat,lng}] (anel aberto) para [[lng,lat], ..., [lng0,lat0]] (fechado).

// [{lat,lng}] (aberto) → polygon-clipping ring [[lng,lat], ..., [lng0,lat0]] (fechado)
export function coordsToPCRing(coords) {
  const cleaned = dedupeAndOpen(coords)
  if (cleaned.length < 3) return []
  const ring = cleaned.map(c => [Number(c.lng), Number(c.lat)])
  ring.push([Number(cleaned[0].lng), Number(cleaned[0].lat)])
  return ring
}

// [{lat,lng}] (aberto) → polygon-clipping Polygon [[ [lng,lat], ..., [lng0,lat0] ]]
export function coordsToPCPolygon(coords) {
  const ring = coordsToPCRing(coords)
  return ring.length >= 4 ? [ring] : []
}

// polygon-clipping Multipolygon → array de [{lat,lng}] (cada elemento é um anel exterior aberto)
export function pcMultiPolyToCoordsList(multi) {
  if (!Array.isArray(multi)) return []
  // Multipolygon = [ Polygon1, Polygon2, ... ], Polygon = [ outerRing, hole1, hole2, ... ]
  return multi
    .map(poly => {
      const outer = poly?.[0] || []
      const coords = outer.map(p => ({ lat: Number(p[1]), lng: Number(p[0]) }))
      return dedupeAndOpen(coords)
    })
    .filter(c => c.length >= 3)
}
